import { api } from '@/lib/axios';

export interface Board {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  color: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  steps?: BoardStep[];
  flows?: { id: string; name: string }[];
}

export interface BoardStep {
  id: string;
  tenantId: string;
  boardId: string;
  name: string;
  description: string | null;
  color: string | null;
  order: number;
  triggerFlowId: string | null;
  triggerFlowDelay: number | null;
  createdAt: string;
  updatedAt: string;
  _count?: { executions: number };
  triggerFlow?: { id: string; name: string } | null;
}

export interface BoardKanbanView {
  board: {
    id: string;
    name: string;
    description: string | null;
    color: string | null;
  };
  columns: BoardKanbanColumn[];
}

export interface BoardKanbanColumn {
  step: {
    id: string;
    name: string;
    description: string | null;
    color: string | null;
    order: number;
    triggerFlowId: string | null;
  };
  cards: BoardKanbanCard[];
  totalCount: number;
}

export interface BoardKanbanCard {
  executionId: string;
  client: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
  };
  flow: {
    id: string;
    name: string;
  };
  status: string;
  createdAt: string;
  updatedAt: string;
  lastResponseAt: string | null;
  messagesCount?: number;
}

export interface ExecutionDetails {
  execution: {
    id: string;
    status: string;
    currentNodeId: string | null;
    startedAt: string | null;
    completedAt: string | null;
    error: string | null;
    createdAt: string;
    updatedAt: string;
  };
  client: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
  };
  flow: {
    id: string;
    name: string;
    description: string | null;
  };
  currentStep: {
    id: string;
    name: string;
    color: string | null;
  } | null;
  messages: {
    id: string;
    direction: string;
    content: string;
    messageType: string;
    sentAt: string;
    delivered: boolean;
    read: boolean;
  }[];
  responses: {
    id: string;
    nodeId: string;
    responseText: string;
    isValid: boolean;
    receivedAt: string;
  }[];
}

export interface BoardStats {
  board: { id: string; name: string };
  totalExecutions: number;
  stepCounts: { stepId: string; stepName: string; count: number }[];
  statusCounts: { status: string; count: number }[];
}

export interface CreateBoardDto {
  name: string;
  description?: string;
  color?: string;
}

export interface UpdateBoardDto {
  name?: string;
  description?: string;
  color?: string;
  active?: boolean;
}

export interface CreateBoardStepDto {
  boardId: string;
  name: string;
  description?: string;
  color?: string;
  order?: number;
  triggerFlowId?: string;
  triggerFlowDelay?: number;
}

export interface UpdateBoardStepDto {
  name?: string;
  description?: string;
  color?: string;
  order?: number;
  triggerFlowId?: string | null;
  triggerFlowDelay?: number;
}

export type BoardStepActionType = 'WEBHOOK' | 'WHATSAPP' | 'EMAIL' | 'NOTIFICATION' | 'FLOW';

export interface BoardStepAction {
  id: string;
  tenantId: string;
  stepId: string;
  actionType: BoardStepActionType;
  name: string;
  description: string | null;
  active: boolean;
  order: number;
  delayMinutes: number;
  config: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBoardStepActionDto {
  stepId: string;
  actionType: BoardStepActionType;
  name: string;
  description?: string;
  active?: boolean;
  order?: number;
  delayMinutes?: number;
  config?: Record<string, any>;
}

export interface UpdateBoardStepActionDto {
  actionType?: BoardStepActionType;
  name?: string;
  description?: string;
  active?: boolean;
  order?: number;
  delayMinutes?: number;
  config?: Record<string, any>;
}

export interface BoardViewQuery {
  status?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}

export const boardsService = {
  // Boards CRUD
  async getAll(params?: { active?: boolean; includeSteps?: boolean; includeFlows?: boolean }): Promise<Board[]> {
    const response = await api.get<Board[]>('/boards', { params });
    return response.data;
  },

  async getById(id: string): Promise<Board> {
    const response = await api.get<Board>(`/boards/${id}`);
    return response.data;
  },

  async create(data: CreateBoardDto): Promise<Board> {
    const response = await api.post<Board>('/boards', data);
    return response.data;
  },

  async update(id: string, data: UpdateBoardDto): Promise<Board> {
    const response = await api.patch<Board>(`/boards/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/boards/${id}`);
  },

  // Board Steps
  async createStep(data: CreateBoardStepDto): Promise<BoardStep> {
    const response = await api.post<BoardStep>('/boards/steps', data);
    return response.data;
  },

  async updateStep(stepId: string, data: UpdateBoardStepDto): Promise<BoardStep> {
    const response = await api.patch<BoardStep>(`/boards/steps/${stepId}`, data);
    return response.data;
  },

  async deleteStep(stepId: string): Promise<void> {
    await api.delete(`/boards/steps/${stepId}`);
  },

  async reorderSteps(boardId: string, stepIds: string[]): Promise<Board> {
    const response = await api.patch<Board>(`/boards/${boardId}/steps/reorder`, { stepIds });
    return response.data;
  },

  // Kanban View
  async getKanbanView(boardId: string, query?: BoardViewQuery): Promise<BoardKanbanView> {
    const response = await api.get<BoardKanbanView>(`/boards/${boardId}/kanban`, { params: query });
    return response.data;
  },

  async getBoardStats(boardId: string): Promise<BoardStats> {
    const response = await api.get<BoardStats>(`/boards/${boardId}/stats`);
    return response.data;
  },

  // Move Executions
  async moveExecution(executionId: string, targetStepId: string): Promise<any> {
    const response = await api.post('/boards/executions/move', { executionId, targetStepId });
    return response.data;
  },

  async bulkMoveExecutions(executionIds: string[], targetStepId: string): Promise<any> {
    const response = await api.post('/boards/executions/bulk-move', { executionIds, targetStepId });
    return response.data;
  },

  // Execution Details
  async getExecutionDetails(executionId: string): Promise<ExecutionDetails> {
    const response = await api.get<ExecutionDetails>(`/boards/executions/${executionId}`);
    return response.data;
  },

  // Link Flow to Board
  async linkFlowToBoard(flowId: string, boardId: string, initialStepId?: string): Promise<any> {
    const response = await api.post('/boards/link-flow', { flowId, boardId, initialStepId });
    return response.data;
  },

  async unlinkFlowFromBoard(flowId: string): Promise<void> {
    await api.post(`/boards/unlink-flow/${flowId}`);
  },

  // Step Actions
  async getStepActions(stepId: string): Promise<BoardStepAction[]> {
    const response = await api.get<BoardStepAction[]>(`/boards/steps/${stepId}/actions`);
    return response.data;
  },

  async createStepAction(data: CreateBoardStepActionDto): Promise<BoardStepAction> {
    const response = await api.post<BoardStepAction>('/boards/steps/actions', data);
    return response.data;
  },

  async updateStepAction(actionId: string, data: UpdateBoardStepActionDto): Promise<BoardStepAction> {
    const response = await api.patch<BoardStepAction>(`/boards/steps/actions/${actionId}`, data);
    return response.data;
  },

  async deleteStepAction(actionId: string): Promise<void> {
    await api.delete(`/boards/steps/actions/${actionId}`);
  },
};
